import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { environment } from 'src/app/environments/environment';
import { SharedUtilityService } from 'src/app/@shared/services/sharedUtility/shared-utility.service';
import {
  getEmployeeDisplayName,
  getLoginEmployeeId,
  getLoginEntityId,
} from 'src/app/onboarding-process/utils/onboarding.util';

const API_BASE = `${environment.servralUrl}/api/v1`;

export interface PolicyConsentItem {
  id: number;
  title: string;
  policy_file_url?: string;
  policy_file_name?: string;
  createdAt?: string;
  signed_at?: string;
  emp_signature?: string;
}

@Injectable({
  providedIn: 'root',
})
export class PolicyConsentService {
  private pendingRequest$: Observable<PolicyConsentItem[]> | null = null;

  constructor(
    private http: HttpClient,
    private sharedUtilityService: SharedUtilityService
  ) {}

  getPendingPolicies(force = false): Observable<PolicyConsentItem[]> {
    if (force) {
      this.clearPendingCache();
    }
    if (this.pendingRequest$) {
      return this.pendingRequest$;
    }

    const employeeId = getLoginEmployeeId();
    const entityId = getLoginEntityId();
    if (employeeId == null) {
      return of([]);
    }

    this.pendingRequest$ = this.http
      .get<any>(
        `${API_BASE}/get_pending_policy_consent?employee_id=${employeeId}&entity_id=${entityId || ''}`
      )
      .pipe(
        map((res) => (Array.isArray(res?.data) ? res.data : [])),
        tap({
          error: () => {
            this.pendingRequest$ = null;
          },
        }),
        shareReplay(1)
      );

    return this.pendingRequest$;
  }

  hasPendingConsent(force = false): Observable<boolean> {
    return this.getPendingPolicies(force).pipe(
      map((list) => list.length > 0),
      catchError(() => of(false))
    );
  }

  getConsentedPolicies(): Observable<PolicyConsentItem[]> {
    const employeeId = getLoginEmployeeId();
    const entityId = getLoginEntityId();
    if (employeeId == null) {
      return of([]);
    }

    return this.http
      .get<any>(
        `${API_BASE}/get_consented_policies?employee_id=${employeeId}&entity_id=${entityId || ''}`
      )
      .pipe(map((res) => (Array.isArray(res?.data) ? res.data : [])));
  }

  submitConsent(payload: {
    employee_id: string | number;
    policy_id: string | number;
    entity_id?: string | number | null;
    emp_signature: string;
    created_by?: string | number;
  }): Observable<any> {
    return this.http.post(`${API_BASE}/create_policy_consent`, payload).pipe(
      tap(() => this.clearPendingCache())
    );
  }

  submitConsentsForListedPolicies(policies: unknown[]): Observable<void> {
    const employeeId = getLoginEmployeeId();
    const entityId = getLoginEntityId();
    const signature = getEmployeeDisplayName();
    const policyIds = this.extractListedPolicyIds(policies);

    if (employeeId == null || !signature || !policyIds.length) {
      return of(void 0);
    }

    const createdBy = this.sharedUtilityService.getUpdatedByField();
    return forkJoin(
      policyIds.map((policyId) =>
        this.submitConsent({
          employee_id: employeeId,
          policy_id: policyId,
          entity_id: entityId,
          emp_signature: signature,
          created_by: createdBy,
        }).pipe(catchError(() => of(null)))
      )
    ).pipe(
      tap(() => this.clearPendingCache()),
      map(() => void 0)
    );
  }

  clearPendingCache(): void {
    this.pendingRequest$ = null;
  }

  private extractListedPolicyIds(policies: unknown[]): number[] {
    const ids = (Array.isArray(policies) ? policies : [])
      .map((item) => {
        if (!item || typeof item !== 'object') {
          return null;
        }
        const row = item as Record<string, unknown>;
        const raw = row['id'] ?? row['policy_id'];
        const id = Number(raw);
        return Number.isFinite(id) && id > 0 ? id : null;
      })
      .filter((id): id is number => id != null);
    return [...new Set(ids)];
  }
}
